/** Chaves auxiliares do rascunho; nunca representam campos novos do template. */
const PREFIX = 'observacao:';
export const observationKey = (fieldId: string): string => `${PREFIX}${fieldId}`;
export const observationFieldId = (key: string): string | null => key.startsWith(PREFIX) ? key.slice(PREFIX.length) : null;
export const MAX_OBSERVATION_LENGTH = 10000;

/** Ausência preserva a coluna; null limpa a nota e valor vazio limpa a escolha. */
export interface ChecklistAnswerPatch {
  valor?: string | null;
  observacao?: string | null;
  expected_partner_revision?: number;
}

export const hasAnswerObservation = (value: unknown): value is ChecklistAnswerPatch & { observacao: string | null } =>
  isAnswerPatch(value) && Object.prototype.hasOwnProperty.call(value, 'observacao');

export const isAnswerPatch = (value: unknown): value is ChecklistAnswerPatch => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const patch = value as Record<string, unknown>;
  const keys = Object.keys(patch);
  return keys.length > 0 && keys.some(key => key === 'valor' || key === 'observacao') &&
    keys.every(key => key === 'valor' || key === 'observacao' || key === 'expected_partner_revision') &&
    keys.every(key => key === 'expected_partner_revision'
      ? Number.isSafeInteger(patch[key]) && Number(patch[key]) >= 0
      : patch[key] === null || typeof patch[key] === 'string');
};

/** Lê o valor efetivo sem descartar a observação da resposta pendente. */
export const checklistAnswerValue = (answer: unknown): unknown => isAnswerPatch(answer) ? answer.valor : answer;

/** Compara somente atributos presentes, inclusive envelopes de nota sem escolha. */
export const checklistAnswerMatchesRow = (answer: unknown, row: { valor?: unknown; observacao?: string | null }): boolean => {
  if (!isAnswerPatch(answer)) return JSON.stringify(row.valor) === JSON.stringify(answer);
  return (!Object.prototype.hasOwnProperty.call(answer, 'valor') || JSON.stringify(row.valor) === JSON.stringify(answer.valor)) &&
    (!hasAnswerObservation(answer) || (row.observacao ?? null) === answer.observacao);
};

/** Compõe patches por atributo para que mudanças independentes não se apaguem. */
export const mergeChecklistAnswerChanges = (previous: Record<string, unknown>, next: Record<string, unknown>): Record<string, unknown> => {
  const merged = { ...previous };
  Object.entries(next).forEach(([id, value]) => {
    const before = previous[id];
    const beforeRevision = isAnswerPatch(before) ? before.expected_partner_revision : undefined;
    const nextRevision = isAnswerPatch(value) ? value.expected_partner_revision : undefined;
    if (before !== undefined && beforeRevision !== nextRevision && (beforeRevision !== undefined || nextRevision !== undefined)) {
      throw new Error('partner_pending_revision_mismatch');
    }
    if (isAnswerPatch(value)) {
      merged[id] = { ...(isAnswerPatch(before) ? before : typeof before === 'string' || before === null ? { valor: before } : {}), ...value };
    } else if (isAnswerPatch(before) && (typeof value === 'string' || value === null)) {
      merged[id] = { ...before, valor: value };
    } else {
      merged[id] = value;
    }
  });
  return merged;
};

/** Atualiza escolhas explícitas pela transação sem acrescentar uma escolha a notas isoladas. */
export const mergeChecklistAnswerValuesFromRows = <T extends {
  campo_id?: string | null; snapshot_field_id?: string | null; valor?: unknown;
}>(data: unknown, rows: readonly T[]): unknown => {
  const wasString = typeof data === 'string';
  let parsed: unknown = data;
  if (wasString) { try { parsed = JSON.parse(data); } catch { return data; } }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return data;
  const answers = { ...(parsed as Record<string, unknown>) };
  rows.forEach(row => {
    const fieldId = row.snapshot_field_id || row.campo_id;
    if (fieldId && isAnswerPatch(answers[fieldId]) && Object.prototype.hasOwnProperty.call(answers[fieldId], 'valor')) {
      answers[fieldId] = { ...answers[fieldId], valor: row.valor ?? null };
    }
  });
  return wasString ? JSON.stringify(answers) : answers;
};

export const checklistValuesToDraft = (rows: Array<{ campo_id?: string | null; snapshot_field_id?: string | null; valor?: unknown; observacao?: string | null }>): Record<string, string> => {
  const draft: Record<string, string> = {};
  rows.forEach(row => {
    const id = row.snapshot_field_id || row.campo_id;
    if (!id) return;
    if (typeof row.valor === 'string') draft[id] = row.valor;
    if (row.observacao !== undefined) draft[observationKey(id)] = row.observacao ?? '';
  });
  return draft;
};
